import React from "react";
import { Link } from "react-router-dom";
import AppHeader from "@/components/AppHeader";
import { useI18n } from "@/lib/i18n";
import { UserX, Home as HomeIcon, Mail } from "lucide-react";

/**
 * Publicly reachable page for account deletion, required by store review
 * policies (Apple App Store §5.1.1(v), Google Play Data Safety).
 *
 * Route: `/account-deletion` (no auth required).
 *
 * The page explains the process. Signed-in users can trigger deletion
 * directly from Settings → «Elimina account» which calls DELETE /api/me.
 * Users not signed in are directed to email info@toclean.app so the
 * team can process the request manually.
 */
export default function AccountDeletionPage() {
  const { t } = useI18n();
  return (
    <div className="min-h-screen bg-slate-50" data-testid="account-deletion-page">
      <AppHeader />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
        <div className="inline-flex items-center gap-2 chip bg-rose-50 text-rose-800 border border-rose-200 mb-3">
          <UserX size={12} /> {t("acct_del_kicker")}
        </div>
        <h1 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 leading-tight mb-4">
          {t("acct_del_title")}
        </h1>
        <p className="text-slate-700 text-base leading-relaxed mb-4">{t("acct_del_intro")}</p>
        <div className="bg-white border border-slate-200 rounded-2xl p-5 mb-4">
          <div className="font-heading font-semibold text-slate-900 text-lg mb-2">{t("acct_del_what_happens_title")}</div>
          <ul className="text-slate-700 text-sm leading-relaxed space-y-1.5 list-disc pl-5">
            <li>{t("acct_del_what_1")}</li>
            <li>{t("acct_del_what_2")}</li>
            <li>{t("acct_del_what_3")}</li>
            <li>{t("acct_del_what_4")}</li>
            <li>{t("acct_del_what_5")}</li>
          </ul>
        </div>
        <div className="bg-white border border-slate-200 rounded-2xl p-5 mb-4">
          <div className="font-heading font-semibold text-slate-900 text-lg mb-2">{t("acct_del_how_signed_in_title")}</div>
          <p className="text-slate-700 text-sm leading-relaxed mb-3">{t("acct_del_how_signed_in_body")}</p>
          <Link to="/settings" data-testid="acct-del-settings-link" className="inline-flex items-center h-10 px-4 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold">
            {t("acct_del_go_settings")}
          </Link>
        </div>
        <div className="bg-white border border-slate-200 rounded-2xl p-5 mb-4">
          <div className="font-heading font-semibold text-slate-900 text-lg mb-2">{t("acct_del_how_email_title")}</div>
          <p className="text-slate-700 text-sm leading-relaxed mb-3">{t("acct_del_how_email_body")}</p>
          <a href="mailto:info@toclean.app?subject=Richiesta%20cancellazione%20account%20ToClean" data-testid="acct-del-email-link" className="inline-flex items-center gap-2 h-10 px-4 rounded-full bg-slate-900 hover:bg-slate-800 text-white text-sm font-semibold">
            <Mail size={14} /> info@toclean.app
          </a>
        </div>
        <Link to="/" data-testid="acct-del-back-home" className="inline-flex items-center gap-2 h-11 px-5 rounded-full bg-white border border-slate-200 text-slate-700 text-sm font-semibold hover:bg-slate-50 mt-3">
          <HomeIcon size={14} /> {t("missionpage_back_home")}
        </Link>
      </div>
    </div>
  );
}
